/**
 * Badge — status pills, used everywhere.
 *
 * Tone is semantic. Use:
 *   - success: paid, verified, active
 *   - warning: pending, expiring soon
 *   - danger:  overdue, rejected, violation
 *   - info:    processing, in-progress
 *   - primary: highlighted but neutral (e.g. "Selected")
 *   - neutral: generic / placeholder
 *   - violet / amber / sky: domain-specific (vehicle / electric / water)
 *
 * The `dot` prefix is on by default — it gives badges a status-light quality
 * that scans well at table density. Disable with `dot={false}` for pure labels.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

const TONE_CLASSES = {
  neutral:  'bg-stone-100   text-stone-700   [&_.dot]:bg-stone-400',
  primary:  'bg-teal-50  text-teal-700 [&_.dot]:bg-teal-600',
  success:  'bg-green-50  text-green-700 [&_.dot]:bg-green-600',
  warning:  'bg-amber-50  text-amber-700 [&_.dot]:bg-amber-600',
  danger:   'bg-red-50   text-red-700  [&_.dot]:bg-red-600',
  info:     'bg-blue-50     text-blue-700    [&_.dot]:bg-blue-600',
  electric: 'bg-amber-50 text-amber-700 [&_.dot]:bg-amber-500',
  water:    'bg-sky-50    text-blue-700    [&_.dot]:bg-sky-500',
  vehicle:  'bg-violet-50  text-violet-600 [&_.dot]:bg-violet-500',
  violation:'bg-rose-50 text-rose-600 [&_.dot]:bg-rose-500',
};

const SIZE_CLASSES = {
  sm: 'text-2xs px-1.5 py-px gap-1',
  md: 'text-xs  px-2   py-0.5 gap-1.5',
};

export function Badge({
  tone = 'neutral',
  size = 'md',
  dot = true,
  className = '',
  children,
  ...rest
}) {
  return (
    <span
      className={[
        'inline-flex items-center font-medium leading-none rounded',
        TONE_CLASSES[tone],
        SIZE_CLASSES[size],
        className,
      ].join(' ')}
      {...rest}
    >
      {dot && <span className="dot w-1.5 h-1.5 rounded-full shrink-0" />}
      {children}
    </span>
  );
}

/**
 * Convenience aliases for the most common payment statuses. These keep the
 * label text in sync across the app (no more 6 spellings of "Đã thanh toán").
 *
 * Override by passing `children` if you need a custom label.
 */
export function PaymentBadge({ status, ...rest }) {
  const { t } = useTranslation('common');
  const map = {
    paid:           { tone: 'success', label: t('payment.paid') },
    pending:        { tone: 'warning', label: t('payment.pending') },
    overdue:        { tone: 'danger',  label: t('payment.overdue') },
    partial:        { tone: 'info',    label: t('payment.partial') },
    pending_verify: { tone: 'primary', label: t('payment.pendingVerify') },
    refunded:       { tone: 'neutral', label: t('payment.refunded') },
    cancelled:      { tone: 'neutral', label: t('payment.cancelled') },
  };
  const cfg = map[status] || { tone: 'neutral', label: status };
  return <Badge tone={cfg.tone} {...rest}>{rest.children || cfg.label}</Badge>;
}

export function ContractBadge({ status, ...rest }) {
  const { t } = useTranslation('common');
  const map = {
    active:    { tone: 'success', label: t('contract.active') },
    expiring:  { tone: 'warning', label: t('contract.expiring') },
    draft:     { tone: 'neutral', label: t('contract.draft') },
    ended:     { tone: 'neutral', label: t('contract.ended') },
    cancelled: { tone: 'danger',  label: t('contract.cancelled') },
  };
  const cfg = map[status] || { tone: 'neutral', label: status };
  return <Badge tone={cfg.tone} {...rest}>{rest.children || cfg.label}</Badge>;
}

export function RoomBadge({ status, ...rest }) {
  const { t } = useTranslation('common');
  const map = {
    occupied:    { tone: 'success', label: t('room.occupied') },
    vacant:      { tone: 'warning', label: t('room.vacant') },
    maintenance: { tone: 'danger',  label: t('room.maintenance') },
    reserved:    { tone: 'vehicle', label: t('room.reserved') },
  };
  const cfg = map[status] || { tone: 'neutral', label: status };
  return <Badge tone={cfg.tone} {...rest}>{rest.children || cfg.label}</Badge>;
}
